import { X } from 'lucide-react';
import { FilterCondition, FilterField } from '../../queries/marketingSegments';
import { SubscriptionPackage } from '../../queries/subscriptionPackages';

// The billing statuses the Subscriptions page shows and filters by - these
// are what the segment query now derives per user. The previous list
// ('active', 'inactive', 'suspended', 'cancelled') described
// users.subscription_status, a column nothing maintains: it reads 'inactive'
// for every user, so those filters matched either everybody or nobody.
const SUBSCRIPTION_STATUS_OPTIONS = ['Active', 'Past Due', 'Trialing', 'Expired', 'Cancelled', 'Unpaid', 'Promotional'];
const STRIPE_STATUS_OPTIONS = ['active', 'trialing', 'past_due', 'canceled', 'unpaid', 'incomplete'];

const FIELD_LABELS: Record<FilterField, string> = {
  subscription_status: 'Subscription status',
  current_package: 'Current package',
  last_login_days_ago: 'Last login (days ago)',
  registration_date: 'Registration date',
  has_marketing_consent: 'Has marketing consent',
  has_ever_subscribed: 'Has ever subscribed',
  has_ever_paid: 'Has ever paid',
  last_subscription_status: 'Last subscription status',
};

interface SegmentConditionEditorProps {
  condition: FilterCondition;
  packages: SubscriptionPackage[];
  onChange: (condition: FilterCondition) => void;
  onRemove: () => void;
}

// Simplified vs the backend's full operator set (buildSegmentQuery also
// supports not_equals/not_in) - multi-value fields always use "in" here,
// covering the common case without a full general-purpose operator picker.
export function SegmentConditionEditor({ condition, packages, onChange, onRemove }: SegmentConditionEditorProps) {
  const changeField = (field: FilterField) => {
    switch (field) {
      case 'subscription_status':
      case 'last_subscription_status':
      case 'current_package':
        onChange({ field, operator: 'in', value: [] });
        break;
      case 'has_marketing_consent':
      case 'has_ever_subscribed':
      case 'has_ever_paid':
        onChange({ field, operator: 'equals', value: true });
        break;
      case 'registration_date':
        onChange({ field, operator: 'greater_than', value: '' });
        break;
      case 'last_login_days_ago':
        onChange({ field, operator: 'greater_than', value: 0 });
        break;
    }
  };

  const toggleChip = (value: string) => {
    const current = Array.isArray(condition.value) ? (condition.value as string[]) : [];
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    onChange({ ...condition, value: next });
  };

  const chipOptions =
    condition.field === 'subscription_status'
      ? SUBSCRIPTION_STATUS_OPTIONS
      : condition.field === 'last_subscription_status'
        ? STRIPE_STATUS_OPTIONS
        : condition.field === 'current_package'
          ? packages.map((p) => p.package_code)
          : [];

  return (
    <div className="flex flex-wrap items-start gap-2 rounded-md border border-input p-2">
      <select
        value={condition.field}
        onChange={(e) => changeField(e.target.value as FilterField)}
        className="rounded-md border border-input bg-background px-2 py-1 text-sm"
      >
        {Object.entries(FIELD_LABELS).map(([field, label]) => (
          <option key={field} value={field}>{label}</option>
        ))}
      </select>

      {chipOptions.length > 0 && (
        <div className="flex flex-1 flex-wrap gap-1">
          {chipOptions.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => toggleChip(option)}
              className={`rounded-full border px-2 py-0.5 text-xs font-medium ${
                (condition.value as string[])?.includes(option)
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-input text-muted-foreground hover:border-foreground/30'
              }`}
            >
              {condition.field === 'current_package' ? packages.find((p) => p.package_code === option)?.name ?? option : option}
            </button>
          ))}
        </div>
      )}

      {(condition.field === 'has_marketing_consent' || condition.field === 'has_ever_subscribed') && (
        <select
          value={String(condition.value)}
          onChange={(e) => onChange({ ...condition, value: e.target.value === 'true' })}
          className="rounded-md border border-input bg-background px-2 py-1 text-sm"
        >
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      )}

      {condition.field === 'registration_date' && (
        <>
          <select
            value={condition.operator}
            onChange={(e) => onChange({ ...condition, operator: e.target.value as FilterCondition['operator'], value: e.target.value === 'between' ? ['', ''] : '' })}
            className="rounded-md border border-input bg-background px-2 py-1 text-sm"
          >
            <option value="greater_than">After</option>
            <option value="less_than">Before</option>
            <option value="between">Between</option>
          </select>
          {condition.operator === 'between' ? (
            <>
              <input
                type="date"
                value={(condition.value as string[])?.[0] ?? ''}
                onChange={(e) => onChange({ ...condition, value: [e.target.value, (condition.value as string[])?.[1] ?? ''] })}
                className="rounded-md border border-input bg-background px-2 py-1 text-sm"
              />
              <input
                type="date"
                value={(condition.value as string[])?.[1] ?? ''}
                onChange={(e) => onChange({ ...condition, value: [(condition.value as string[])?.[0] ?? '', e.target.value] })}
                className="rounded-md border border-input bg-background px-2 py-1 text-sm"
              />
            </>
          ) : (
            <input
              type="date"
              value={condition.value as string}
              onChange={(e) => onChange({ ...condition, value: e.target.value })}
              className="rounded-md border border-input bg-background px-2 py-1 text-sm"
            />
          )}
        </>
      )}

      {condition.field === 'last_login_days_ago' && (
        <>
          <select
            value={condition.operator}
            onChange={(e) => onChange({ ...condition, operator: e.target.value as FilterCondition['operator'] })}
            className="rounded-md border border-input bg-background px-2 py-1 text-sm"
          >
            <option value="greater_than">More than</option>
            <option value="less_than">Less than</option>
          </select>
          <input
            type="number"
            min={0}
            value={condition.value as number}
            onChange={(e) => onChange({ ...condition, value: Number(e.target.value) })}
            className="w-20 rounded-md border border-input bg-background px-2 py-1 text-sm"
          />
        </>
      )}

      <button type="button" onClick={onRemove} className="ml-auto text-muted-foreground hover:text-red-500" title="Remove condition">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
